// FTU floating checklist (US-033, US-035).
//
// Composes the DS-D `FtuChecklist` composite with:
//   - `useFtuChecklist()` — derives the items + done state from the user.
//   - localStorage persistence for the collapsed flag (per-device pref).
//   - Click handlers: navigates to the target route OR triggers the demo-
//     data seed when the special `demo-data` item is clicked.
//
// The demo-data CTA on the footer reuses the same handler.

import { useCallback, useState } from 'react';

import { useSeedDemo } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { FtuChecklist, toast } from '@barel/ui';
import { useNavigate } from '@tanstack/react-router';

import {
  readFtuChecklistCollapsed,
  writeFtuChecklistCollapsed,
} from '../onboarding/lib/persistence';

import { useFtuChecklist } from './useFtuChecklist';

export interface ChecklistProps {
  /**
   * Hide the widget — the parent shell decides this from
   * `users.onboardingCompletedAt` + dismissal state.
   */
  hidden?: boolean;
}

export function FtuChecklistContainer({ hidden }: ChecklistProps) {
  const t = useT('ftu');
  const navigate = useNavigate();
  const seed = useSeedDemo();
  const checklist = useFtuChecklist();
  const [collapsed, setCollapsed] = useState<boolean>(() => readFtuChecklistCollapsed());

  const handleLoadDemo = useCallback(async () => {
    try {
      await seed.mutateAsync();
      toast.success({ title: t('demo.loadedToast') });
      checklist.track('__demo__');
    } catch {
      toast.error({ title: t('demo.errorToast') });
    }
  }, [seed, t, checklist]);

  const handleItemClick = useCallback(
    (id: string) => {
      if (id === 'demo-data') {
        void handleLoadDemo();
        return;
      }
      const target = checklist.resolveTarget(id);
      if (!target) return;
      void navigate({ to: target });
    },
    [checklist, handleLoadDemo, navigate],
  );

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      writeFtuChecklistCollapsed(next);
      return next;
    });
  }, []);

  if (hidden) return null;

  return (
    <FtuChecklist
      items={checklist.items}
      collapsed={collapsed}
      onToggleCollapsed={toggleCollapsed}
      onItemClick={handleItemClick}
      onLoadDemo={handleLoadDemo}
      title={t('checklist.title')}
      completeMessage={t('checklist.completeMessage')}
      remainingTemplate={(n) => t('checklist.remaining', { count: n })}
      loadDemoLabel={t('demo.loadButton')}
      footerCaption={t('checklist.footerCaption')}
      aria-label={t('checklist.ariaLabel')}
      toggleAriaLabel={t('checklist.toggleAriaLabel')}
    />
  );
}
